import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../hooks/useAuth";
import { logService } from "../utils/logService";

const validationFields = [
  {
    section: "Agent Validation & Performance",
    entries: [
      "Reputation Score",
      "Failure Rate",
      "Violations",
      "Provenance",
      "Comments",
      "Verification Status",
    ],
  },
];

function AgentValidation() {
  const [form, setForm] = useState({});
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole, isModerator } = useAuth();

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        if (!id) {
          navigate("/agents");
          return;
        }

        const agentRef = doc(db, "agents", id);
        const agentSnap = await getDoc(agentRef);

        if (agentSnap.exists()) {
          const agentData = agentSnap.data();
          setAgent({ id: agentSnap.id, ...agentData });

          // Pre-populate form with existing validation data
          const validationData = {};
          validationFields[0].entries.forEach((field) => {
            if (agentData[field]) {
              validationData[field] = agentData[field];
            }
          });
          setForm(validationData);
        } else {
          toast.error("Agent not found");
          navigate("/agents");
        }
      } catch (error) {
        console.error("Error fetching agent:", error);
        toast.error("Error loading agent data");
        navigate("/agents");
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, [id, navigate]);

  const handleChange = (e, label) => {
    setForm({ ...form, [label]: e.target.value });
  };

  const validateForm = () => {
    const requiredFields = validationFields[0].entries;
    const missingFields = requiredFields.filter(
      (field) => !form[field]?.trim()
    );

    if (missingFields.length > 0) {
      const fieldNames = missingFields.join(", ");
      toast.error(`Please fill all required fields`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (!agent) {
        toast.error("Agent data not loaded");
        return;
      }

      const agentRef = doc(db, "agents", agent.id);

      // Update agent with validation data
      await updateDoc(agentRef, {
        ...form,
        lastUpdated: new Date().toISOString(),
      });

      // Log the validation addition
      await logService.createLog(
        logService.actions.VALIDATION_ADDED,
        {
          agentId: agent.id,
          agentName: agent["Agent Name"] || "Unnamed Agent",
          validationData: form,
        },
        {
          uid: auth.currentUser?.uid,
          email: auth.currentUser?.email,
          displayName: auth.currentUser?.displayName,
        }
      );

      toast.success("Agent validation data saved successfully!");

      // Redirect to agent list page
      setTimeout(() => {
        navigate("/agents");
      }, 1500);
    } catch (error) {
      console.error("Error saving validation data:", error);
      toast.error("Error saving validation data");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">
            Loading agent data...
          </span>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-valia-primary mb-4">
          Agent Not Found
        </h1>
        <button
          onClick={() => navigate("/agents")}
          className="valia-btn-primary valia-btn-sm"
        >
          Back to Agents
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-valia-primary">
                Agent Validation
              </h1>
            </div>
            <p className="text-valia-gray-600 text-base">
              Add validation and performance data for:{" "}
              <span className="font-medium text-valia-primary">
                {agent["Agent Name"] || "Unnamed Agent"}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Agent Info */}
      <div className="valia-card mb-6">
        <div className="valia-card-header">
          <h3 className="text-lg font-semibold text-valia-gray-900">
            Agent Information
          </h3>
        </div>
        <div className="valia-card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-valia-gray-600 text-sm">Agent Name</p>
              <p className="text-valia-gray-900 font-medium">
                {agent["Agent Name"] || "—"}
              </p>
            </div>
            <div>
              <p className="text-valia-gray-600 text-sm">Agent Type</p>
              <p className="text-valia-gray-900 font-medium">
                {agent["Agent Type"] || "—"}
              </p>
            </div>
            <div>
              <p className="text-valia-gray-600 text-sm">Current Status</p>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                  agent["Verification Status"] === "Verified"
                    ? "valia-badge-success border-green-300"
                    : agent["Verification Status"] === "Pending"
                    ? "valia-badge-warning border-orange-300"
                    : agent["Verification Status"] === "Under Review"
                    ? "valia-badge-info border-blue-300"
                    : agent["Verification Status"] === "Unverified"
                    ? "valia-badge-error border-red-300"
                    : "valia-badge-gray border-gray-400"
                }`}
              >
                {agent["Verification Status"] || "Unknown"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Form */}
      <form onSubmit={(e) => e.preventDefault()}>
        {validationFields.map((section, idx) => (
          <div key={idx} className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-valia-primary">
              {section.section}
            </h2>
            <div className="valia-table overflow-hidden">
              <table className="table-auto w-full border-collapse">
                <thead>
                  <tr className="bg-valia-gray-50">
                    <th className="px-4 py-3 w-1/3 text-left text-valia-gray-700 font-semibold">
                      Field
                    </th>
                    <th className="px-4 py-3 text-left text-valia-gray-700 font-semibold">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.entries.map((label, i) => (
                    <tr
                      key={i}
                      className="border-b border-valia-gray-200 hover:bg-valia-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-valia-gray-700">
                        {label}
                        <span className="text-valia-error ml-1">*</span>
                      </td>
                      <td className="px-4 py-3">
                        {label === "Verification Status" ? (
                          <select
                            className="valia-input"
                            value={form[label] || ""}
                            onChange={(e) => handleChange(e, label)}
                          >
                            <option value="">Select verification status</option>
                            <option value="Pending">Pending</option>
                            <option value="Under Review">Under Review</option>
                            <option value="Verified">Verified</option>
                            <option value="Unverified">Unverified</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="valia-input"
                            value={form[label] || ""}
                            onChange={(e) => handleChange(e, label)}
                            placeholder={`Enter ${label.toLowerCase()}`}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => navigate("/agents")}
            className="valia-btn-secondary valia-btn-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="valia-btn-primary valia-btn-sm"
          >
            Save Validation Data
          </button>
        </div>
      </form>
    </div>
  );
}

export default AgentValidation;
